import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { DebitCardOptions, Homeless } from 'types';

import AssignCardPopUp from '../holder_profile/profile_2.0/Funds/AssignCardPopUp';
import Card from '../_shared/Card';
import Celebrate from '../../assets/celebrate.png';
import Confetti from '../_shared/Confetti';
import CreateActionStepsPopup from './CreateActionStepsPopup';
import OptionalAction from './OptionalAction';
import PopUp from '../holder_profile/profile_2.0/PopUp';

import { useDebitCardActions } from 'hooks/useDebitCardActions';

import Button from '@mui/material/Button';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        actionCardContainer: {
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            background: '#FBFBFF',
            maxWidth: '640px',
            margin: '20px auto !important',
            padding: '26px 20px 20px 20px !important',
            zIndex: 3
        },
        actions: {
            display: 'flex',
            justifyContent: 'space-between',
            maxWidth: '800px',
            '@media (max-width: 768px)': {
                flexDirection: 'column'
            }
        },
        btnContainer: {
            padding: '20px 0 40px 0'
        },
        cardTitle: {
            position: 'absolute',
            top: '-56px',
            left: '24px',
            background: '#8882D8',
            borderRadius: '32px',
            color: '#FFFFFF',
            fontFamily: 'Manrope',
            fontSize: '18px',
            fontWeight: 800,
            letterSpacing: '0.94px',
            margin: '2.375rem 0 0',
            padding: '3px 38px',
            textTransform: 'uppercase'
        },
        congratsPage: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: '#2F365B',
            backgroundImage: `url(${Celebrate})`,
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat',
            minHeight: '100vh',
            overflowY: 'auto',
            width: '100%',
            zIndex: 2,
            '@media (min-height: 1200px)': {
                justifyContent: 'center' // Move content to the top for smaller screens
            }
        },
        memberName: {
            color: '#FBFBFF',
            fontSize: '2.5rem',
            fontWeight: 700,
            margin: '20px',
            padding: '20px',
            width: '60%',
            textAlign: 'center'
        },
        memberPhoto: {
            backgroundColor: '#FFFFFF',
            border: '4px solid #FFFFFF',
            borderRadius: '50%',
            height: '320px',
            margin: '30px 0 50px 0',
            width: '320px',
            '& img': {
                borderRadius: '50%',
                height: '320px',
                objectFit: 'cover',
                width: '320px'
            }
        },
        profileBtn: {
            background: 'linear-gradient(180deg, #8571DF 0%, #7378E8 100%)',
            borderRadius: '36px',
            boxShadow: '0px 8px 16px 4px rgba(0, 0, 0, 0.08)',
            color: '#FBFBFF',
            fontSize: '24px',
            fontWeight: 700,
            padding: '12px 102px',
            textTransform: 'none',
            zIndex: 3,
            '&:hover': {
                background: 'linear-gradient(180deg, #8571DF 0%, #918ED7 100%)'
            }
        }
    })
);

type CongratsPageProps = {
    debitCardOptions: DebitCardOptions[];
    homelesses: Homeless[];
    intakeEmail: string;
    intakeId: number;
    intakeProfilePic: File | string;
    memberName: string;
};

enum ModalType {
    None = "NONE",
    DebitOptions = "DEBIT_OPTIONS",
    AssignDebit = "ASSIGN_DEBIT",
    CreateActionsSteps = "CREATE_ACTIONS_STEPS",
}

const CongratsPage: React.FC<CongratsPageProps> = ({
    debitCardOptions,
    homelesses,
    intakeEmail,
    intakeId,
    intakeProfilePic,
    memberName
}) => {
    const classes = useStyles();
    const history = useHistory();

    const { success } = useDebitCardActions();
    const [modalType, setModalType] = useState<ModalType>(ModalType.None);
    const [didAssignDebit, setDidAssignDebit] = useState<boolean>(false);
    const [amountOfActionSteps, setAmountOfActionSteps] = useState<number>(0);
    const didCreateActionsSteps = useMemo(() => {
        return amountOfActionSteps > 0;
    }, [amountOfActionSteps]);
    const [debitType, setDebitType] = useState<
        'givecard' | 'pex' | 'virtual-givecard' | null
    >(null);

    const holder = Array.isArray(homelesses)
        ? homelesses.find((homeless) => homeless.id === intakeId)
        : undefined;
    const organization = holder ? holder.organization : null;

    const handleCardClick = () => {
        if (didAssignDebit) {
            return;
        }

        setModalType(ModalType.DebitOptions);
    };

    const handleActionStepsClick = () => {
        if (didCreateActionsSteps) {
            return;
        }   
        setModalType(ModalType.CreateActionsSteps);
    };

    const handleRenderProfilePicture = () => {
        if (intakeProfilePic) {
            return typeof intakeProfilePic === 'string'
                ? intakeProfilePic
                : URL.createObjectURL(intakeProfilePic);
        }

        return holder?.photo;
    };

    const possessiveMemberName = (name: string) => {
        if (!name) return '';

        if (name.toLowerCase().endsWith('s')) {
            return `${name}'`;
        } else {
            return `${name}'s`;
        }
    };

    const photo = handleRenderProfilePicture();

    useEffect(() => {
        //change the modal type to assign debit if the debit type is set
        if (debitType) {
            setModalType(ModalType.AssignDebit);
        }
    }, [debitType]);

    useEffect(() => {
        if (success) {
            setDidAssignDebit(true);
        }
    }, [success]);

    return (
        <div className={classes.congratsPage}>
            <Confetti stopConfetti={false} />
            <span className={classes.memberName}>
                {possessiveMemberName(memberName)} Membership is now live! 🎉
            </span>
            <span className={classes.memberPhoto}>
                <img src={photo} alt="member-profile" />
            </span>
            <Card className={classes.actionCardContainer}>
                <span className={classes.cardTitle}>Optional Actions</span>
                <div className={classes.actions}>
                    <OptionalAction
                        type="action_steps"
                        memberName={possessiveMemberName(memberName)}
                        handleCardClick={handleActionStepsClick}
                        completed={didCreateActionsSteps}
                        completedText={`${amountOfActionSteps} action step${amountOfActionSteps > 1 ? 's' : ''} created`}
                    />
                    <OptionalAction
                        type="debit"
                        memberName={possessiveMemberName(memberName)}
                        handleCardClick={handleCardClick}
                        completed={didAssignDebit}
                        completedText="Card Assigned"
                    />
                </div>
            </Card>
            <div className={classes.btnContainer}>
                <Button
                    className={classes.profileBtn}
                    variant="contained"
                    onClick={() => history.push(`/holders/${intakeId}`)}
                    aria-label="Go to profile"
                >
                    Go to Profile
                </Button>
            </div>
            {modalType === ModalType.DebitOptions && holder && (
                <AssignCardPopUp
                    debitCardOptions={debitCardOptions}
                    handleModalClick={() => {
                        // reset the modal type and the debit type
                        setModalType(ModalType.None);
                        setDebitType(null);
                    }}
                    holder={holder}
                    intakeEmail={intakeEmail}
                    photo={holder ? holder.photo : ''}
                    setDebitType={setDebitType}
                    usingVirtualCards={
                        organization?.using_virtual_cards || false
                    }
                />
            )}
            {holder && modalType === ModalType.AssignDebit && (
                <PopUp
                    content="debit card"
                    debitCardOptions={debitCardOptions}
                    debitType={debitType}
                    handleModalClick={() => setModalType(ModalType.None)}
                    holder={holder}
                    setAssignDebit={setDidAssignDebit}
                    setDebitType={setDebitType}
                />
            )}
            {modalType === ModalType.CreateActionsSteps && (
                <CreateActionStepsPopup
                    holderId={intakeId}
                    orgId={organization?.id}
                    setAmountOfActionSteps={setAmountOfActionSteps}
                    handleClose={() => setModalType(ModalType.None)}
                />
            )}
        </div>
    );
};

export default CongratsPage;
